import { z } from 'zod';
import { cuidSchema } from './common.js';
/**
 * Contratos da Fase 5: credenciais, conexoes e varreduras.
 *
 * Duas regras atravessam este arquivo inteiro.
 *
 * A primeira: **o segredo nunca volta**. O `clientSecret` entra uma vez, e
 * dali em diante a API devolve apenas o apelido e os quatro ultimos
 * caracteres do `clientId` — o suficiente para a pessoa reconhecer qual par
 * e qual, e insuficiente para qualquer outra coisa.
 *
 * A segunda: **o sistema nao inicia pagamento**. Nada aqui movimenta dinheiro.
 * O Open Finance entra so como leitura, que e o que a especificacao pediu e o
 * que mantem a superficie de risco pequena.
 */
const apelido = z.string().trim().min(2, 'Informe ao menos 2 caracteres.').max(40);
// ------------------------------------------------------------- credenciais
export const createCredentialSchema = z.object({
    /** Como a pessoa reconhece este par: "Victor", "Ana". */
    nickname: apelido,
    clientId: z.string().trim().min(8).max(200),
    clientSecret: z.string().trim().min(8).max(400),
});
export const credentialSchema = z.object({
    id: z.string(),
    nickname: z.string(),
    /** So os quatro ultimos caracteres. O par completo nao volta nunca. */
    clientIdTail: z.string(),
    ownerName: z.string().nullable(),
    isMine: z.boolean(),
    connectionCount: z.number().int(),
    createdAt: z.string(),
});
export const credentialListSchema = z.object({ credentials: z.array(credentialSchema) });
// ---------------------------------------------------------------- conexoes
export const connectionStatusSchema = z.enum([
    'ACTIVE',
    'WAITING_USER',
    'CONSENT_EXPIRED',
    'ERROR',
]);
export const syncStatusSchema = z.enum(['RUNNING', 'SUCCESS', 'FAILED']);
export const syncTriggerSchema = z.enum(['SCHEDULE', 'MANUAL', 'WEBHOOK']);
export const createConnectionSchema = z.object({
    credentialId: cuidSchema,
    /**
     * O `itemId` que a Pluggy devolve ao criar o consentimento.
     *
     * Ele e colado a mao de proposito: `GET /items` responde 403
     * `LIST_ITEMS_FEATURE_NOT_ENABLED` no plano gratuito, entao nao ha como
     * descobrir a lista pelo lado de ca. A API confere o id contra a Pluggy
     * antes de gravar, para um id errado falhar aqui e nao na primeira
     * varredura de madrugada.
     */
    itemId: z.string().trim().min(8).max(100),
    institutionName: z.string().trim().max(120).optional(),
});
export const syncRunSchema = z.object({
    id: z.string(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    status: syncStatusSchema,
    trigger: syncTriggerSchema,
    fetchedCount: z.number().int(),
    createdCount: z.number().int(),
    skippedCount: z.number().int(),
    transferCount: z.number().int(),
    /** Veio da instituicao e nao deu para traduzir. Nao pode sumir calado. */
    unmappedCount: z.number().int(),
    /** A varredura parou no teto de paginas e nao chegou ao fim. */
    pagesExhausted: z.boolean(),
    errorCode: z.string().nullable(),
    errorText: z.string().nullable(),
});
export const connectionSchema = z.object({
    id: z.string(),
    itemId: z.string(),
    institutionName: z.string(),
    credentialId: z.string(),
    credentialNickname: z.string(),
    status: connectionStatusSchema,
    statusDetail: z.string().nullable(),
    consentExpiresAt: z.string().nullable(),
    /** Quantos dias faltam para o consentimento vencer. Negativo: ja venceu. */
    consentDaysLeft: z.number().int().nullable(),
    lastSyncedAt: z.string().nullable(),
    accountCount: z.number().int(),
    lastRun: syncRunSchema.nullable(),
});
export const connectionListSchema = z.object({ connections: z.array(connectionSchema) });
export const syncRunListSchema = z.object({ runs: z.array(syncRunSchema) });
export const syncResultSchema = z.object({
    run: syncRunSchema,
    connection: connectionSchema,
});
// ----------------------------------------------------------------- webhook
/**
 * Corpo do aviso da Pluggy.
 *
 * Lido como **pista**, nunca como verdade: o unico campo aproveitado e o
 * `itemId`, e so depois de conferir que ele pertence a uma conexao
 * registrada. O `createdTransactionsLink` que a Pluggy tambem manda e
 * ignorado de proposito — buscar uma URL que chega de fora e SSRF servido em
 * bandeja, e nao ha nada nele que a varredura normal nao obtenha sozinha.
 */
export const pluggyWebhookSchema = z
    .object({
    event: z.string().max(100).optional(),
    itemId: z.string().trim().min(8).max(100).optional(),
})
    .passthrough();
//# sourceMappingURL=openfinance.js.map